import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { cn } from "@fieldwork/ui/lib/utils";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import z from "zod";

import { Logo } from "@/components/server-shell";
import { Field } from "@/components/ui-bits";
import { authClient, checkSession } from "@/lib/auth-client";

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ next: z.string().optional() }),
  // still signed in (e.g. sent here by an old page): go straight back instead of signing in again
  beforeLoad: async ({ search }) => {
    if ((await checkSession()).state === "signed-in") throw redirect({ href: safeNext(search.next) });
  },
  component: Login,
});

/** Only same-site paths, never another origin or a protocol-relative URL. */
const safeNext = (n?: string) => (n && /^\/(?![/\\])/.test(n) && !n.startsWith("/login") ? n : "/servers");

const INPUT = "h-12 bg-[#1A1A19] px-3.5 text-[15px]";

/** Faint nested squares behind the hero, as on the design canvas. */
function Backdrop() {
  return (
    <svg
      viewBox="-54 -54 108 108"
      width="560"
      height="560"
      aria-hidden="true"
      className="pointer-events-none absolute -right-[180px] -bottom-[160px] opacity-50"
    >
      <g fill="none" stroke="#2A2927" strokeWidth="1.6">
        <polygon points="50,50 -50,50 -50,-50 50,-50" />
        <polygon points="36.33,50 -50,36.33 -36.33,-50 50,-36.33" />
        <polygon points="24.52,48.13 -48.13,24.52 -24.52,-48.13 48.13,-24.52" />
        <polygon points="14.59,44.9 -44.9,14.59 -14.59,-44.9 44.9,-14.59" />
        <polygon points="6.46,40.76 -40.76,6.46 -6.46,-40.76 40.76,-6.46" />
        <polygon points="0,36.07 -36.07,0 0,-36.07 36.07,0" />
        <polygon points="-4.93,31.14 -31.14,-4.93 4.93,-31.14 31.14,4.93" />
        <polygon points="-8.51,26.2 -26.2,-8.51 8.51,-26.2 26.2,8.51" />
      </g>
    </svg>
  );
}

function Hero() {
  return (
    <section
      aria-label="About Fieldwork"
      className="relative box-border flex min-w-0 flex-[1_1_520px] flex-col justify-between gap-12 overflow-hidden bg-[#0C0C0B] px-6 py-10 sm:px-16 sm:py-14"
    >
      <Backdrop />
      <div className="relative flex items-center gap-3">
        <Logo size={34} />
        <span className="font-mono text-lg font-bold">Fieldwork</span>
      </div>
      <div className="relative flex max-w-[520px] flex-col gap-[22px]">
        <h1 className="m-0 font-mono text-[34px] leading-[1.04] font-bold tracking-[-0.04em] sm:text-[46px]">
          Extract tasks from messages, <span className="text-[#FF4F1A]">remove noise.</span>
        </h1>
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-[#CFCBC1]">
          {[
            "Runs on a server in your own cloud account",
            "Your messages, tokens and keys stay on it",
            "It connects out to this panel and opens no ports",
          ].map((t) => (
            <li key={t} className="flex gap-2.5">
              <span aria-hidden="true" className="text-[#FF4F1A]">
                ■
              </span>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <span className="relative font-mono text-xs text-[#8C8981]">by Infinitum Labs · infinitumlabs.studio</span>
    </section>
  );
}

function Login() {
  const [mode, setMode] = useState<"signin" | "request" | "requested">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { next } = Route.useSearch();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        const { error } = await authClient.signIn.email({ email, password });
        if (error) toast.error(error.message || "Could not sign in");
        // a full load (not a router navigate) so the new session cookie is used everywhere straight away
        else window.location.assign(safeNext(next));
      } else {
        if (password.length < 10) return void toast.error("Use at least 10 characters for the password");
        const { error } = await authClient.signUp.email({ name, email, password });
        if (error) toast.error(error.message || "Could not send the request");
        else setMode("requested");
      }
    } finally {
      setBusy(false);
    }
  }

  const tab = mode === "requested" ? "request" : mode;

  return (
    <div className="flex min-h-svh flex-wrap bg-[#111111] text-sm text-[#ECEAE4]">
      <Hero />
      <section className="box-border flex min-w-0 flex-[1_1_440px] items-center justify-center px-6 py-10 sm:px-12 sm:py-14">
        <div className="flex w-full max-w-[400px] flex-col gap-6">
          <div role="tablist" aria-label="Account" className="flex gap-0.5 rounded-md bg-[#1A1A19] p-[3px]">
            {(
              [
                ["signin", "Sign in"],
                ["request", "Request access"],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={tab === m}
                onClick={() => setMode(m)}
                className={cn(
                  "min-h-10 flex-1 cursor-pointer rounded-[4px] border-0 font-mono text-[13px]",
                  tab === m ? "bg-[#2A2927] text-[#ECEAE4]" : "bg-transparent text-[#A8A59D] hover:text-[#ECEAE4]",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === "requested" ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <h2 className="m-0 font-mono text-2xl font-bold">Request sent</h2>
                <p className="m-0 text-[#A8A59D]">
                  Fieldwork is open to a few people for now. Once your account is approved you can sign in with the email
                  and password you just chose.
                </p>
              </div>
              <Button size="lg" variant="outline" className="h-12 self-start" onClick={() => setMode("signin")}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <h2 className="m-0 font-mono text-2xl font-bold">{mode === "signin" ? "Welcome back" : "Request access"}</h2>
                <p className="m-0 text-[#A8A59D]">
                  {mode === "signin"
                    ? "Manage your Fieldwork server."
                    : "New accounts are approved by hand before they can sign in."}
                </p>
              </div>
              <form onSubmit={submit} className="flex flex-col gap-4">
                {mode === "request" && (
                  <Field label="Name">
                    <Input
                      className={INPUT}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      minLength={2}
                      autoComplete="name"
                    />
                  </Field>
                )}
                <Field label="Email">
                  <Input
                    className={INPUT}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </Field>
                <Field label="Password" hint={mode === "request" ? "At least 10 characters." : undefined}>
                  <Input
                    className={INPUT}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  />
                </Field>
                <Button type="submit" size="lg" className="h-12 text-sm" disabled={busy}>
                  {busy ? "…" : mode === "signin" ? "Sign in" : "Request access"}
                </Button>
              </form>
              {mode === "signin" && (
                <div className="flex flex-col gap-1 rounded-md bg-[#1A1A19] px-4 py-3.5 shadow-[inset_0_0_0_1px_#2A2927]">
                  <span className="font-medium">New here?</span>
                  <span className="text-[13px] text-[#A8A59D]">
                    Fieldwork is open to a few people for now.{" "}
                    <button
                      type="button"
                      onClick={() => setMode("request")}
                      className="cursor-pointer border-0 bg-transparent p-0 text-[#ECEAE4] underline underline-offset-2 hover:text-[#FF4F1A]"
                    >
                      Request access
                    </button>{" "}
                    and we’ll approve your account by hand; then sign in with the email and password you chose.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
